// University of Rochester: public college page (#2748), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Rochester's Common Data Set 2025-2026 (CDS-2025-26-completed-for-web.pdf,
//    "Updated June 9, 2026"), linked from the Office of the Provost's Common
//    Data Set page. C1 applicants, admits and enrollees for Fall 2025 with the
//    residency table; C8 test policy for Fall 2027; C21 early decision counts
//    for Fall 2025 (both rounds together); G1/G5 2026-2027 costs.
//  - admissions.rochester.edu: Dates & Deadlines, First-Year Students and
//    Testing Policies. The deadline table carries no year; it is the page as
//    posted on 2026-10-01 for the application now open (fall 2027 entry).
//  - College Scorecard, school id 195030 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Cost: the CDS G1/G5 first-year figures. G5 lists books and supplies as $0,
// "Included in cost of tuition - UR Essentials", so the page shows that row
// at $0 with a note rather than leaving it out.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'University of Rochester Common Data Set 2025-2026',
        url: 'https://www.rochester.edu/provost/university-data/data-insights-reporting/university-of-rochester-common-data-set/',
        read: '2026-10-01',
    },
    dates: {
        label: 'Rochester Admissions, Dates and Deadlines',
        url: 'https://admissions.rochester.edu/applying/dates-and-deadlines/',
        read: '2026-10-01',
    },
    firstYear: {
        label: 'Rochester Admissions, First-Year Students',
        url: 'https://admissions.rochester.edu/applying/first-year-students/',
        read: '2026-10-01',
    },
    tests: {
        label: 'Rochester Admissions, Testing Policies',
        url: 'https://admissions.rochester.edu/applying/testing-policies/',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?195030-University-of-Rochester',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'university-of-rochester',
    kbId: 'university_of_rochester',
    name: 'University of Rochester',
    shortName: 'Rochester',
    aliases: ['URochester', 'U of R'],
    location: 'Rochester, NY',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Rochester color system page); decoration only.
    branding: {
        primary: '#001E5F',
        secondary: '#FFD82B',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/university_of_rochester_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 21609,
        admitted: 9056,
        enrolled: 1231,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 4551, admitted: 2292 },
            { group: 'Out-of-state', applied: 9060, admitted: 5311 },
            { group: 'International', applied: 7998, admitted: 1453 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Rochester\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by school or major.',
        source: 'cds',
    },
    earlyRounds: {
        year: 'Fall 2025',
        title: 'Early decision',
        note: 'The Common Data Set reports Early Decision I and II together for fall 2025. Rochester has no early action plan.',
        rows: [
            { plan: 'Early Decision I and II', applied: 1129, admitted: 472 },
        ],
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'Most applicants use the Common App or Coalition application for Arts, Sciences and Engineering. Two paths work differently:',
        rows: [
            { school: 'Eastman School of Music', requirement: 'To study solely at Eastman for a bachelor of music, apply directly through the Eastman application, not the Common App or Coalition application.' },
            { school: 'Combined degree programs', requirement: 'Name the program in your application and write a short essay about your interest. REMS is due November 15 with decisions in mid-January and is closed to international applicants; Dual Degree Eastman is due January 5 with decisions in mid-March; BMP, GEAR, GRADE and HEAL follow the regular early decision and regular decision dates.', source: 'dates' },
        ],
        source: 'firstYear',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 5' },
        rows: [
            { plan: 'Early Decision I', deadline: 'November 1', decision: 'Mid-December', binding: true },
            { plan: 'Priority scholarship deadline', deadline: 'December 1', decision: 'Late February', binding: false },
            { plan: 'Early Decision II', deadline: 'January 5', decision: 'Early February', binding: true },
            { plan: 'Regular Decision', deadline: 'January 5', decision: 'April 1', binding: false },
        ],
        note: 'Your application is due by the deadline; other materials are due December 1 for Early Decision I, February 1 for Early Decision II and February 15 for Regular Decision. Early decision admits reply within three weeks; everyone else replies by May 1. Applying by December 1 gives the highest merit scholarship consideration. The application fee is $50.',
        source: 'dates',
        binding: 'Early decision is binding: you, a parent or guardian and your school counselor sign the Early Decision Confirmation Agreement, you may apply early decision to only one college, and if admitted you must enroll and withdraw your other applications. If you are denied in early decision, you are not reconsidered in regular decision. Early decision applicants send the FAFSA and CSS Profile by December 1.',
        bindingSource: 'dates',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Rochester is test-optional for every applicant, for admission and scholarships. You choose on the application whether to apply without scores or "test-flexible," sending the SAT, ACT, or two or more results from exams such as AP, IB or A-levels. You may report scores yourself on the application, and Rochester superscores the SAT and the ACT, but never combines the two.',
        source: 'tests',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as not required for fall 2027 applicants, but considered if submitted, with scores due by January 5.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'tests',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 71750 },
            { item: 'Required fees', amount: 1622 },
            { item: 'Food and housing (on campus)', amount: 21572 },
            { item: 'Books and supplies', amount: 0 },
            { item: 'Transportation', amount: 300 },
            { item: 'Other expenses', amount: 1022 },
        ],
        total: 96266,
        note: 'For a full-time first-year student living on campus. Books and supplies are $0 because Rochester includes them in tuition through UR Essentials.',
        source: 'cds',
        where: 'sections G1 and G5',
        netPrice: {
            average: 34347,
            byIncome: [
                { income: '$0 to $30,000', amount: 15091 },
                { income: '$30,001 to $48,000', amount: 15962 },
                { income: '$48,001 to $75,000', amount: 20337 },
                { income: '$75,001 to $110,000', amount: 28472 },
                { income: 'Over $110,000', amount: 51184 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Rochester\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
